import { styled, keyframes } from '@mui/material/styles'
import { Box, IconButton, Paper, Typography } from '@mui/material'

// Animations
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`

const messageAppear = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Chat toggle button (floating)
export const ChatToggleButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'align',
})<{ align?: 'left' | 'right' }>(({ align = 'left' }) => ({
    position: 'fixed',
    bottom: 20,
    ...(align === 'left' ? { left: 20 } : { right: 20 }),
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'var(--card-bg, #fff)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 99999,
    '&:hover': {
        background: 'var(--main-bg-elevated, #f5f5f5)',
        transform: 'scale(1.05)',
    },
    transition: 'all 0.2s ease',
}))

// Chat panel container
export const ChatPanel = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isFullscreen' && prop !== 'align',
})<{ isFullscreen?: boolean; align?: 'left' | 'right' }>(({ isFullscreen, align = 'left' }) => ({
    position: 'fixed',
    bottom: isFullscreen ? 0 : 90,
    ...(align === 'left'
        ? { left: isFullscreen ? 0 : 20 }
        : { right: isFullscreen ? 0 : 20 }),
    width: isFullscreen ? '100%' : 500,
    height: isFullscreen ? '100%' : 600,
    maxWidth: isFullscreen ? '100%' : 'calc(100vw - 40px)',
    maxHeight: isFullscreen ? '100%' : 'calc(100vh - 110px)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: isFullscreen ? 0 : 12,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid var(--card-border, #e2e8f0)',
    zIndex: 99999,
    background: 'var(--main-bg, #f0f4f8)',
}))

// Minimized chat bar (click to expand)
// Offset by FAB width (56px) + gap (8px) so it sits beside the trigger button, not behind it.
export const MinimizedBar = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'align',
})<{ align?: 'left' | 'right' }>(({ align = 'left' }) => ({
    position: 'fixed',
    bottom: 20,
    ...(align === 'left' ? { left: 84 } : { right: 84 }),
    maxWidth: 500,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    zIndex: 99999,
    background: 'var(--main-bg, #f0f4f8)',
    border: '1px solid var(--card-border, #e2e8f0)',
    cursor: 'pointer',
    '&:hover': {
        background: 'var(--main-bg-elevated, #e8eef4)',
    },
}))


// Header
export const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '8px 16px',
    background: 'var(--main-bg, #f0f4f8)',
    borderBottom: '1px solid var(--card-border, #e2e8f0)',
    minHeight: 48,
}))

export const HeaderButton = styled('button')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    border: 'none',
    background: 'none',
    color: 'var(--main-text-secondary, #64748b)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&:hover': {
        color: 'var(--main-accent, #3b82f6)',
        background: 'transparent !important',
    },
}))

// Messages container
export const MessagesContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: 16,
    background: 'var(--main-bg, #f0f4f8)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
}))

// Message wrapper
export const MessageWrapper = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== 'isUser' &&
        prop !== 'isCollapsed' &&
        prop !== 'isRouterBubble',
})<{ isUser?: boolean; isCollapsed?: boolean; isRouterBubble?: boolean }>(
    ({ isUser, isCollapsed, isRouterBubble }) => ({
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: 12,
        animation: `${messageAppear} 0.3s ease forwards`,
        ...(isRouterBubble &&
            isCollapsed && {
                '& .message-bubble': {
                    maxHeight: 60,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 30,
                        background:
                            'linear-gradient(to bottom, transparent, var(--main-bg, #f8fafc))',
                    },
                },
            }),
    }),
)

// Avatar
export const MessageAvatar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser?: boolean }>(({ isUser }) => ({
    width: 40,
    height: 40,
    borderRadius: 8,
    background: 'var(--main-bg-elevated, #e0e7ff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
        fontSize: 20,
        color: 'var(--main-accent, #4f46e5)',
    },
}))

// Message bubble
export const MessageBubble = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser?: boolean }>(({ isUser }) => ({
    background: isUser ? 'var(--main-bg-elevated, #eef2ff)' : 'var(--card-bg, #ffffff)',
    padding: 16,
    borderRadius: 12,
    maxWidth: '80%',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    '& p': {
        margin: 0,
        marginBottom: 8,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    '& pre': {
        background: 'var(--tooltip-bg, #1e293b)',
        color: 'var(--tooltip-text, #ffffff)',
        padding: 12,
        borderRadius: 8,
        overflow: 'auto',
        fontSize: 14,
    },
    '& code': {
        background: 'var(--main-bg-elevated, #f1f5f9)',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 14,
    },
    '& a': {
        color: 'var(--link-color, #3b82f6)',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    '& .loading-message': {
        color: 'var(--main-text-secondary, #64748b)',
        fontStyle: 'italic',
    },
}))

// Message timestamp
export const MessageTimestamp = styled(Typography)(({ theme }) => ({
    fontSize: 11,
    color: 'var(--main-text-tertiary, #94a3b8)',
    marginTop: 4,
}))

// Typing indicator
export const TypingIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: 4,
    padding: '4px 0',
    '& span': {
        width: 8,
        height: 8,
        background: 'var(--main-text-tertiary, #94a3b8)',
        borderRadius: '50%',
        animation: `${bounce} 1s infinite`,
        '&:nth-of-type(2)': {
            animationDelay: '0.2s',
        },
        '&:nth-of-type(3)': {
            animationDelay: '0.4s',
        },
    },
}))

// Input container
export const InputContainer = styled(Box)(({ theme }) => ({
    padding: 16,
    background: 'var(--main-bg, #f0f4f8)',
    borderTop: '1px solid var(--card-border, #e2e8f0)',
}))

export const InputWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'var(--card-bg, #fff)',
    borderRadius: 12,
    padding: '4px 4px 4px 16px',
    border: '1px solid var(--card-border, #e2e8f0)',
    '&:focus-within': {
        borderColor: 'var(--main-accent, #3b82f6)',
        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
    },
}))

export const ChatInput = styled('textarea')(({ theme }) => ({
    flex: 1,
    outline: 'none',
    fontSize: 14,
    padding: '12px 0',
    resize: 'none',
    minHeight: 24,
    maxHeight: 120,
    lineHeight: 1.5,
    fontFamily: 'inherit',
    // && doubles the generated class name (.css-xyz.css-xyz), raising specificity to (0,2,0)
    // which beats the global [data-theme] input rule at (0,1,1) even when both use !important
    '&&': {
        background: 'transparent !important',
        border: 'none !important',
        color: 'var(--main-text, #1e293b)',
    },
    '&::placeholder': {
        color: 'var(--main-text-tertiary, #94a3b8)',
    },
}))

export const ComposerActions = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
})

export const SendButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'isLoading',
})<{ isLoading?: boolean }>(({ isLoading }) => ({
    width: 40,
    height: 40,
    background: isLoading ? 'var(--error-color, #ef4444)' : 'var(--main-accent, #3b82f6)',
    color: 'var(--button-text, #fff)',
    borderRadius: 8,
    '&:hover': {
        background: isLoading ? 'var(--error-color, #dc2626)' : 'var(--main-accent-hover, #2563eb)',
    },
    '&:disabled': {
        background: 'var(--main-text-tertiary, #cbd5e1)',
        color: 'var(--button-text, #fff)',
    },
}))

// Toolbar
export const Toolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '8px 16px',
    background: 'var(--main-bg, #f0f4f8)',
    borderTop: '1px solid var(--card-border, #e2e8f0)',
}))

export const ToolbarButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    border: 'none',
    background: isActive
        ? 'var(--info-bg, rgba(59, 130, 246, 0.12))'
        : 'none',
    borderRadius: 6,
    color: isActive
        ? 'var(--main-accent, #3b82f6)'
        : 'var(--main-text-secondary, #64748b)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    '&:hover': {
        color: 'var(--main-accent, #3b82f6)',
        background: 'transparent !important',
    },
    '& svg': {
        fontSize: 16,
    },
}))

// Connection status indicator
export const StatusDot = styled('span', {
    shouldForwardProp: (prop) => prop !== 'status',
})<{ status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'invalid_config' }>(
    ({ status }) => ({
        width: 8,
        height: 8,
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: 6,
        background:
            status === 'connected'
                ? 'var(--success-color, #10b981)'
                : status === 'connecting'
                ? 'var(--warning-color, #f59e0b)'
                : 'var(--error-color, #ef4444)',
    }),
)

// Explain badge — sits at the top-right corner of the chat FAB like a notification dot.
// Chat FAB: bottom:20, left/right:20, size:56×56 → top-right corner: bottom:76, left/right:76.
// Badge size 22px → center at bottom:65, left/right:65.
export const ExplainToggleFAB = styled('button', {
    shouldForwardProp: (prop) => prop !== 'align' && prop !== 'isActive',
})<{ align?: 'left' | 'right'; isActive?: boolean }>(({ align = 'left', isActive }) => ({
    position: 'fixed',
    bottom: 65,
    ...(align === 'left' ? { left: 65 } : { right: 65 }),
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: 'none',
    background: isActive
        ? 'var(--main-accent, #3b82f6)'
        : 'var(--main-text-secondary, #64748b)',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
    zIndex: 100000,
    transition: 'background 0.2s ease, transform 0.15s ease',
    userSelect: 'none',
    padding: 0,
    '&:hover': {
        background: 'var(--main-accent, #3b82f6)',
        transform: 'scale(1.15)',
    },
    '& svg': {
        fontSize: 13,
    },
}))

// Logo image
export const LogoImage = styled('img')({
    width: 40,
    height: 40,
    objectFit: 'contain',
})

// Assistant badge
export const AssistantBadge = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    background: 'var(--main-accent, #3b82f6)',
    color: 'var(--button-text, #fff)',
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 8,
}))

// Tool call badge — shown in agent mode for each MCP tool invocation
export const ToolCallBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'status',
})<{ status: 'running' | 'completed' | 'failed' }>(({ status }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.3px',
    background:
        status === 'completed'
            ? 'var(--success-bg, #dcfce7)'
            : status === 'failed'
            ? 'var(--error-bg, #fee2e2)'
            : 'var(--warning-bg, #fef3c7)',
    color:
        status === 'completed'
            ? 'var(--success-color, #166534)'
            : status === 'failed'
            ? 'var(--error-color, #991b1b)'
            : 'var(--warning-color, #92400e)',
    border: `1px solid ${
        status === 'completed'
            ? 'var(--success-color, #bbf7d0)'
            : status === 'failed'
            ? 'var(--error-color, #fecaca)'
            : 'var(--warning-color, #fde68a)'
    }`,
    transition: 'background-color 0.2s ease, color 0.2s ease',
}))
